import type { ReactElement, ReactNode } from "react";
import { logical } from "./metrics.ts";

/**
 * A label tooltip (the design's `Tooltip`, a visual reference; ADR-068's amendment of 2026-09-27): a short label in
 * Noto Sans at the body role in the atlas's `tooltip` frame, placed absolutely in its positioned parent with its
 * foot a given height above the parent's bottom, and taking no pointer events. It never carries the only copy of
 * what it says: whatever shows it also carries the label as its accessible name, and a click, a tap or keyboard
 * focus shows it as well as a hover (ADR-061, nothing shown only on hover).
 *
 * @param props The tooltip's `id` and its `text`; `children`, which follow the lines (a bar or a second section); optionally `lines`, which set it as stacked lines in place of `text`
 *   (a card: the first line is the heading); and its place in logical px: `left` from the parent's left edge or
 *   `right` from its right edge across, and `bottom` from the parent's bottom edge or `top` from its top edge down.
 * @returns The tooltip.
 * @example
 * <Tooltip id="ot-chat-scope-tip" text="Local" left={4} bottom={40} />
 */
export function Tooltip({ id, text, lines, left, bottom, right, top, children }: { readonly children?: ReactNode; readonly id: string; readonly text: string; readonly lines?: readonly string[] | undefined; readonly left?: number | undefined; readonly bottom?: number | undefined; readonly right?: number | undefined; readonly top?: number | undefined }): ReactElement {
  return (
    <span id={id} role="tooltip" className="ot-tooltip ot-ui-frame-tooltip" data-kit="tooltip" style={{ left: left === undefined ? undefined : logical(left), bottom: bottom === undefined ? undefined : logical(bottom), right: right === undefined ? undefined : logical(right), top: top === undefined ? undefined : logical(top) }}>
      {lines === undefined ? text : lines.map((line, index) => (
        <span key={index} className={index === 0 ? "ot-tooltip__line ot-tooltip__line--head" : "ot-tooltip__line"}>
          {line}
        </span>
      ))}
      {children}
    </span>
  );
}
